import { Field, Fieldset, Label, Legend } from '@headlessui/react';
import { useAlert } from '@src/components/Alert';
import { GCashButton } from '@src/components/GCashButton';
import { LottieSpinner } from '@src/components/LottieSpinner';
import {
  type Balance,
  CurrencyIcon,
  format,
  formattedOrderTime,
  Portal,
  QuoteStatusEnum,
  resetSwapForm,
  selectCurrenciesIncrement,
  selectEffectiveMaxBalanceArgs,
  selectSecurityAndSide,
  sendLimitOrderThunk,
  setSwapFromCurrency,
  setSwapFromQuantity,
  setSwapMarketAccount,
  setSwapStep,
  setSwapToCurrency,
  setSwapToQuantity,
  switchSwapSides,
  toBigWithDefault,
  useBalancesContext,
  useConstant,
  useCurrencyConversionRateValue,
  useObservableValue,
  useSwapDependencies,
  useWLEffectiveMaxBalance,
  useWLHomeCurrency,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
} from '@talos/kyoko';
import Big from 'big.js';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { map, of } from 'rxjs';
import { AnimatedStackOutlet } from '../../components/AnimatedStackOutlet';
import { Dropdown } from '../../components/Dropdown';
import { InlineFormattedNumber } from '../../components/InlineFormattedNumber';
import { Input } from '../../components/Input';
import { RotatingButton } from '../../components/RotatingButton';
import { StackScreen } from '../../components/StackScreen';
import { useStackableRouteHeaderRightPortalId } from '../../hooks/StackScreenContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSpotlight } from '../../hooks/useSpotlight';
import { SwapConfirmationPage } from './SwapConfirmation';

export const SwapRoute = () => {
  return (
    <Routes>
      <Route path="*" element={<SwapLayout />}>
        <Route
          path="confirmation"
          element={
            <StackScreen initialTitle="GCrypto+" canGoBack={false}>
              <SwapConfirmationPage />
            </StackScreen>
          }
        />
      </Route>
    </Routes>
  );
};

const SwapLayout = () => {
  const navigate = useNavigate();
  return (
    <>
      <AnimatedStackOutlet />
      <div className="flex size-full flex-1 grow" data-testid="swap-form-page">
        {/* Base background route: always rendered */}
        <SwapPage onOrderSent={() => navigate('confirmation', { replace: true })} />
      </div>
    </>
  );
};

const quickButtons = [
  {
    label: '25%',
    value: '0.25',
  },
  {
    label: '50%',
    value: '0.5',
  },
  {
    label: '75%',
    value: '0.75',
  },
  {
    label: 'Max',
    value: '1',
  },
] as const;

export const SwapPage = ({ onOrderSent }: { onOrderSent: () => void }) => {
  useSwapDependencies();

  const dispatch = useWLOrderFormDispatch();
  const fromCurrency = useWLOrderFormSelector(state => state.swap.form.fromCurrency);
  const toCurrency = useWLOrderFormSelector(state => state.swap.form.toCurrency);
  const marketAccount = useWLOrderFormSelector(state => state.swap.form.marketAccount);
  const fromQty = useWLOrderFormSelector(state => state.swap.form.fromQty);
  const toQty = useWLOrderFormSelector(state => state.swap.form.toQty);
  const swapStep = useWLOrderFormSelector(state => state.swap.meta.step);
  const quote = useWLOrderFormSelector(state => state.swap.quote);
  const securityAndSide = useWLOrderFormSelector(selectSecurityAndSide);

  const { homeCurrency } = useWLHomeCurrency();

  const effectiveMaxBalanceArgs = useWLOrderFormSelector(selectEffectiveMaxBalanceArgs);
  const effectiveMax = useWLEffectiveMaxBalance(effectiveMaxBalanceArgs);

  const fromMaxAvailable =
    securityAndSide?.security.BaseCurrency === fromCurrency.value?.Symbol
      ? effectiveMax.effectiveMaxBase
      : securityAndSide?.security.QuoteCurrency === fromCurrency.value?.Symbol
        ? effectiveMax.effectiveMaxQuote
        : undefined;
  const fromConversion = useCurrencyConversionRateValue(homeCurrency, fromCurrency.value?.Symbol);
  const toConversion = useCurrencyConversionRateValue(homeCurrency, toCurrency.value?.Symbol);
  const homeCurrencyMaxAvailableEquivalent = fromConversion?.Rate
    ? Big(fromMaxAvailable || 0)
        .div(Big(fromConversion.Rate))
        .toFixed(2)
    : undefined;

  const { fromCurrencyDetails } = useWLOrderFormSelector(selectCurrenciesIncrement);

  const rotatingButtonRef = useRef<HTMLButtonElement>(null!);
  const spotlight = useSpotlight();
  const [hasShownOrderFormSpotlight, setHasShownOrderFormSpotlight] = useLocalStorage('shown-swap-spotlight', false);

  const { balancesByMarketAccountCurrency: balancesByMarketAccountCurrencyObs } = useBalancesContext();

  const balanceByCurrency = useObservableValue(
    () =>
      marketAccount.value
        ? balancesByMarketAccountCurrencyObs.pipe(
            map(balancesByMarketAccountCurrency => {
              const balancesByCurrency =
                marketAccount.value?.SourceAccountID &&
                balancesByMarketAccountCurrency.get(marketAccount.value.SourceAccountID);
              return balancesByCurrency || new Map<string, Balance>([]); // Ensure we always return a Map
            })
          )
        : of(new Map<string, Balance>()),
    [balancesByMarketAccountCurrencyObs, marketAccount],
    new Map<string, Balance>([])
  );

  const toCurrencyBalance = toCurrency.value?.Symbol ? balanceByCurrency.get(toCurrency.value?.Symbol) : undefined;

  const homeCurrencyToBalanceEquivalent = toConversion?.Rate
    ? Big(toCurrencyBalance?.Amount || 0)
        .div(Big(toConversion.Rate || 1))
        .toFixed(2)
    : undefined;

  const spotlightText = useConstant(
    <div className="text-left">
      <div className="text-2xl">Easily trade coins!</div>
      <div>Tap the button to switch the two tokens selected.</div>
    </div>
  );

  // first time user, show the spotlight
  useEffect(() => {
    if (!hasShownOrderFormSpotlight && import.meta.env.VITE_IS_TEST_ENVIRONMENT === 'false') {
      setTimeout(() => {
        spotlight.focus(rotatingButtonRef.current, {
          content: spotlightText,
        });
      }, 1000);
      setHasShownOrderFormSpotlight(true);
    }
  }, [hasShownOrderFormSpotlight, spotlight, spotlightText, setHasShownOrderFormSpotlight]);
  const { open } = useAlert();
  const onClickContinueButton = useCallback(async () => {
    if (swapStep !== 'locked') {
      dispatch(setSwapStep('locking'));
      return;
    }
    if (swapStep === 'locked') {
      const response = await dispatch(sendLimitOrderThunk());
      if (sendLimitOrderThunk.fulfilled.match(response)) {
        onOrderSent();
        return;
      }
      if (sendLimitOrderThunk.rejected.match(response)) {
        const errorMsg = typeof response.payload === 'string' ? response.payload : response.error.message;
        open({
          title: 'Error',
          message: errorMsg,
        });
        dispatch(resetSwapForm({ keepFromQty: true }));
      }
      return;
    }
  }, [swapStep, dispatch, onOrderSent, open]);
  const quoteCountdown = useWLOrderFormSelector(state => state.swap.meta.quoteCountdown);

  const headerRightPortalId = useStackableRouteHeaderRightPortalId();
  const pct = useMemo(
    () =>
      ({
        '0.25': format(toBigWithDefault(fromMaxAvailable, 0).mul(0.25).toString(), {
          round: false,
          spec: fromCurrencyDetails?.minIncrement,
          pretty: false,
          truncate: true,
        }),
        '0.5': format(toBigWithDefault(fromMaxAvailable, 0).mul(0.5).toString(), {
          round: false,
          spec: fromCurrencyDetails?.minIncrement,
          pretty: false,
          truncate: true,
        }),
        '0.75': format(toBigWithDefault(fromMaxAvailable, 0).mul(0.75).toString(), {
          round: false,
          spec: fromCurrencyDetails?.minIncrement,
          pretty: false,
          truncate: true,
        }),
        '1': format(fromMaxAvailable, {
          round: false,
          spec: fromCurrencyDetails?.minIncrement,
          pretty: false,
          truncate: true,
        }),
      }) as const,
    [fromMaxAvailable, fromCurrencyDetails?.minIncrement]
  );

  const shouldDisableFields =
    swapStep === 'locking' || swapStep === 'locked' || swapStep === 'sendingEntity' || swapStep === 'sentEntity';

  return (
    <Fragment>
      <AnimatePresence>
        {(swapStep === 'locking' || swapStep === 'sendingEntity') && (
          <motion.div
            data-testid="swap-spinner"
            className="bg-black-100 fixed inset-0 z-[1000] grid place-items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <LottieSpinner />
          </motion.div>
        )}
      </AnimatePresence>
      <Portal portalId={headerRightPortalId}>
        <button
          onClick={() => {
            spotlight.focus(rotatingButtonRef.current, {
              content: spotlightText,
            });
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M9.00049 0.100006C13.9156 0.100217 17.9009 4.08519 17.9009 9.0004C17.9007 13.9154 13.9155 17.9006 9.00049 17.9008C4.08528 17.9008 0.100309 13.9156 0.100098 9.0004C0.100098 4.08506 4.08515 0.100006 9.00049 0.100006ZM9.00049 1.90079C5.07927 1.90079 1.90088 5.07917 1.90088 9.0004C1.90109 12.9214 5.0794 16.1 9.00049 16.1C12.9214 16.0998 16.0999 12.9213 16.1001 9.0004C16.1001 5.07931 12.9215 1.901 9.00049 1.90079ZM9.08252 7.2045C9.48561 7.24577 9.80029 7.58641 9.80029 8.0004V12.2006H10.0005L10.0825 12.2045C10.4856 12.2458 10.8003 12.5864 10.8003 13.0004C10.8001 13.4142 10.4855 13.7551 10.0825 13.7963L10.0005 13.8002H8.00049C7.55879 13.8002 7.20089 13.442 7.20068 13.0004C7.20068 12.5586 7.55866 12.2006 8.00049 12.2006H8.20068V8.8002H8.00049C7.55879 8.8002 7.20089 8.44205 7.20068 8.0004C7.20068 7.55857 7.55866 7.20059 8.00049 7.20059H9.00049L9.08252 7.2045ZM9.00049 4.0004C9.55259 4.00061 10.0005 4.44824 10.0005 5.0004C10.0003 5.55237 9.55246 6.00019 9.00049 6.0004C8.44833 6.0004 8.0007 5.5525 8.00049 5.0004C8.00049 4.44811 8.4482 4.0004 9.00049 4.0004Z"
              fill="white"
            />
          </svg>
        </button>
      </Portal>
      <div className="mx-auto flex w-full flex-1 flex-col p-4 text-sm">
        <Fieldset className="shadow-depth-0 border-color-border bg-color-bg-main w-full rounded-lg border p-4">
          {marketAccount.availableItems.length > 1 && (
            <>
              <Legend>Market Account</Legend>
              <div className="flex flex-row gap-2">
                <Field className="relative">
                  <Dropdown
                    disabled={marketAccount.isDisabled || shouldDisableFields}
                    data-testid="market-account-dropdown"
                    onChange={marketAcct => {
                      dispatch(setSwapMarketAccount(marketAcct));
                    }}
                    options={marketAccount.availableItems}
                    selected={marketAccount.value}
                    getLabel={mktAcc => mktAcc.SourceAccountID}
                    getKey={mktAcc => mktAcc.MarketAccountID.toString()}
                  />
                </Field>
              </div>
            </>
          )}

          <Legend className="text-color-text font-semibold">From</Legend>
          <div className="flex flex-row gap-2">
            <Field className="relative">
              <Dropdown
                data-testid="swap-from-currency-dropdown"
                onChange={c => {
                  dispatch(setSwapFromCurrency(c));
                }}
                options={fromCurrency.availableItems}
                selected={fromCurrency.value}
                getDescription={currency => currency.Description}
                getLabel={currency => currency.Symbol}
                getKey={currency => currency.Symbol}
                getPrefix={currency => <CurrencyIcon currency={currency.Symbol} />}
                disabled={fromCurrency.isDisabled || shouldDisableFields}
              />
            </Field>

            <Field className="flex-1">
              <Input
                data-testid="swap-from-qty"
                onChange={qty => {
                  dispatch(setSwapFromQuantity({ qty, setTouched: true }));
                }}
                disabled={fromQty.isDisabled || shouldDisableFields}
                scale={fromQty.scale}
                value={fromQty.value}
                error={
                  (Big(fromQty.value || 0).gt(fromMaxAvailable ?? 0) ? "You don't have enough balance" : '') ||
                  (fromQty.hasInvalidValue && fromQty.errorString)
                }
                suffix={
                  <>
                    <Label className="text-color-text-weakest flex h-4 gap-1">
                      Balance:{' '}
                      <span className="text-color-text-weak font-semibold">
                        <InlineFormattedNumber currency={fromCurrency.value?.Symbol} number={fromMaxAvailable} />
                      </span>
                    </Label>
                    <Label className="text-color-text-weakest flex h-4 gap-1">
                      {fromQty.errorString}
                      {homeCurrencyMaxAvailableEquivalent && homeCurrency !== fromCurrency.value?.Symbol && (
                        <>
                          Approx: ~
                          <span className="text-color-text-weak font-semibold">
                            <InlineFormattedNumber
                              showCurrency
                              currency={homeCurrency}
                              number={homeCurrencyMaxAvailableEquivalent}
                            />
                          </span>
                        </>
                      )}
                    </Label>
                  </>
                }
              />
            </Field>
          </div>

          <div className="flex flex-row gap-2 pt-2 pb-4">
            {quickButtons.map(({ label, value }) => {
              const isActive =
                !toBigWithDefault(fromQty.value, 0).eq(0) &&
                toBigWithDefault(pct[value], 0).eq(toBigWithDefault(fromQty.value, 0));
              return (
                <button
                  data-testid={`quick-button-${value}`}
                  key={label}
                  onClick={() => {
                    dispatch(setSwapFromQuantity({ qty: pct[value], setTouched: true }));
                  }}
                  disabled={shouldDisableFields || toBigWithDefault(fromMaxAvailable, 0).eq(0)}
                  data-active={isActive}
                  className={`flex-1 rounded-full border py-0.5 text-sm font-semibold disabled:opacity-50 ${
                    isActive
                      ? 'bg-color-bg-primary text-color-text-inverse'
                      : 'border-color-border-weak bg-color-bg-secondary text-color-text-primary'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Fieldset>
        <div className="bg-color-bg-main relative flex h-4 items-center justify-center">
          <div className="relative grid place-items-center">
            <svg
              className="col-span-full row-span-full"
              width="56"
              height="56"
              viewBox="-1 -1 34 34"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="16" cy="16" r="16" stroke="#e5e7eb" fill="#fff" strokeWidth="1" />
            </svg>
            <RotatingButton
              ref={rotatingButtonRef}
              onClick={() => {
                dispatch(switchSwapSides());
              }}
              data-testid="switch-sides-button"
              disabled={shouldDisableFields}
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z"
                  fill="#005CE5"
                />
                <path
                  d="M24.4435 19.7144C24.5314 19.9277 24.5168 20.1724 24.3995 20.3755L22.5245 23.6235L22.4689 23.7056C22.328 23.8882 22.1094 23.9985 21.8751 23.9985H18.1251C17.8574 23.9985 17.6098 23.8552 17.4757 23.6235L15.6007 20.3755C15.4834 20.1724 15.4688 19.9277 15.5567 19.7144L15.6007 19.6255L17.4757 16.3774C17.6097 16.1456 17.8573 16.0024 18.1251 16.0024H21.8751C22.1429 16.0024 22.3905 16.1456 22.5245 16.3774L24.3995 19.6255L24.4435 19.7144ZM21.4425 17.5024H18.5587L17.1163 20.0005L18.5587 22.4985H21.4425L22.8849 20.0005L21.4425 17.5024Z"
                  fill="white"
                />
                <path
                  d="M16.0298 14.9697C16.3227 15.2626 16.3227 15.7374 16.0298 16.0303C15.7369 16.3228 15.2621 16.3231 14.9693 16.0303L14.0005 15.0605L13.9996 23C13.9996 23.4142 13.6638 23.75 13.2496 23.75C12.8356 23.7497 12.4996 23.4141 12.4996 23L12.5005 15.0596L11.5308 16.0303C11.2379 16.3232 10.7622 16.3232 10.4693 16.0303C10.1768 15.7374 10.1767 15.2625 10.4693 14.9697L12.7193 12.7197L12.7379 12.7031C12.7442 12.6971 12.7508 12.6913 12.7574 12.6855L12.7769 12.668C12.7895 12.6577 12.8039 12.65 12.817 12.6406C12.8348 12.6279 12.8527 12.6156 12.8716 12.6045C12.8933 12.5918 12.9155 12.5807 12.938 12.5703C12.9576 12.5613 12.9772 12.5522 12.9976 12.5449C13.0201 12.5369 13.043 12.5312 13.066 12.5254C13.0869 12.5201 13.1078 12.5142 13.1295 12.5107C13.1541 12.5068 13.1788 12.5054 13.2037 12.5039C13.2191 12.503 13.2349 12.5 13.2505 12.5C13.2568 12.5 13.2629 12.5008 13.2691 12.501C13.294 12.5016 13.3186 12.5057 13.3433 12.5088C13.3647 12.5115 13.386 12.5131 13.4068 12.5176C13.4266 12.5218 13.4459 12.5283 13.4654 12.5342C13.4908 12.5418 13.5164 12.5484 13.5406 12.5586C13.5581 12.566 13.5743 12.5762 13.5914 12.585C13.6139 12.5965 13.6366 12.6074 13.6578 12.6211C13.6735 12.6313 13.6876 12.6438 13.7027 12.6553C13.7263 12.6733 13.7497 12.6913 13.7711 12.7119C13.7739 12.7146 13.7771 12.717 13.7798 12.7197L16.0298 14.9697ZM13.9996 26.75C13.9996 27.1642 13.6638 27.5 13.2496 27.5C12.8356 27.4997 12.4996 27.1641 12.4996 26.75L12.5005 26L12.5045 25.9229C12.5431 25.5449 12.8624 25.25 13.2505 25.25C13.6385 25.2502 13.958 25.5451 13.9966 25.9229L14.0005 26L13.9996 26.75ZM29.5298 23.9697C29.8227 24.2626 29.8227 24.7374 29.5298 25.0303L27.2798 27.2803C27.2772 27.2829 27.2737 27.2845 27.2711 27.2871C27.2497 27.3078 27.2263 27.3257 27.2027 27.3438C27.1876 27.3553 27.1735 27.3677 27.1578 27.3779C27.1366 27.3917 27.1139 27.4025 27.0914 27.4141C27.0743 27.4228 27.0581 27.433 27.0406 27.4404C27.0163 27.4506 26.9908 27.4572 26.9654 27.4648C26.9458 27.4707 26.9267 27.4772 26.9068 27.4814C26.886 27.4859 26.8648 27.4876 26.8433 27.4902C26.8185 27.4934 26.794 27.4974 26.7691 27.498C26.7629 27.4982 26.7568 27.5 26.7505 27.5C26.7349 27.5 26.7191 27.4961 26.7037 27.4951C26.6788 27.4936 26.6541 27.4923 26.6295 27.4883C26.6078 27.4848 26.587 27.479 26.566 27.4736C26.543 27.4678 26.5201 27.4621 26.4976 27.4541C26.4771 27.4468 26.4577 27.4377 26.438 27.4287C26.4154 27.4183 26.3933 27.4073 26.3716 27.3945C26.3527 27.3834 26.3348 27.3711 26.317 27.3584C26.304 27.3492 26.2894 27.3422 26.2769 27.332L26.2574 27.3135C26.2508 27.3077 26.2442 27.3019 26.2378 27.2959L26.2193 27.2803L23.9693 25.0303C23.6767 24.7374 23.6767 24.2626 23.9693 23.9697C24.2622 23.6768 24.7379 23.6768 25.0308 23.9697L26.0005 24.9395L25.9996 17C25.9996 16.5859 26.3356 16.2503 26.7496 16.25C27.1638 16.25 27.4996 16.5858 27.4996 17L27.5005 24.9385L28.4693 23.9697C28.7621 23.6769 29.2369 23.6772 29.5298 23.9697ZM27.4966 14.0771C27.458 14.455 27.1385 14.7498 26.7505 14.75C26.3624 14.75 26.0431 14.4551 26.0045 14.0771L26.0005 14L25.9996 13.25C25.9996 12.8359 26.3356 12.5003 26.7496 12.5C27.1638 12.5 27.4996 12.8358 27.4996 13.25L27.5005 14L27.4966 14.0771Z"
                  fill="white"
                />
              </svg>
            </RotatingButton>
          </div>
          <div className="absolute inset-0 bg-[#F6F9FD]" />
        </div>
        <Fieldset className="shadow-depth-0 border-color-border bg-color-bg-main w-full rounded-lg border p-4">
          <Legend className="text-color-text font-semibold">To</Legend>
          <div className="flex flex-row items-stretch gap-2">
            <Field>
              <Dropdown
                data-testid="swap-to-currency-dropdown"
                onChange={c => {
                  dispatch(setSwapToCurrency(c));
                }}
                options={toCurrency.availableItems}
                selected={toCurrency.value}
                getDescription={currency => currency.Description}
                getLabel={currency => currency.Symbol}
                getKey={currency => currency.Symbol}
                getPrefix={currency => <CurrencyIcon currency={currency.Symbol} />}
                disabled={toCurrency.isDisabled || shouldDisableFields}
              />
            </Field>
            <Field className="flex-1">
              <Input
                disabled={toQty.isDisabled || shouldDisableFields}
                data-testid="swap-to-qty"
                scale={toQty.scale}
                onChange={qty => {
                  dispatch(setSwapToQuantity({ qty, setTouched: true }));
                }}
                value={toQty.value}
                error={toQty.hasInvalidValue && toQty.errorString}
                suffix={
                  <>
                    {toCurrencyBalance && (
                      <Label className="text-color-text-weakest flex h-4 gap-1" data-testid="to-currency-balance">
                        Balance:
                        <span className="text-color-text-weak font-semibold">
                          <InlineFormattedNumber
                            showCurrency
                            currency={toCurrency.value?.Symbol}
                            number={toCurrencyBalance.Amount}
                          />
                        </span>
                      </Label>
                    )}
                    {homeCurrencyToBalanceEquivalent && (
                      <>
                        <Label className="text-color-text-weakest flex h-4 gap-1" data-testid="to-currency-approx">
                          Approx: ~
                          <span className="text-color-text-weak font-semibold">
                            <InlineFormattedNumber
                              showCurrency
                              currency={homeCurrency}
                              number={homeCurrencyToBalanceEquivalent}
                            />
                          </span>
                        </Label>
                      </>
                    )}
                  </>
                }
              />
            </Field>
          </div>
        </Fieldset>

        <div className="mt-auto flex flex-col gap-2 pt-2">
          <GCashButton
            data-testid="swap-button"
            size="lg"
            variant="primary"
            className="w-full"
            onClick={onClickContinueButton}
            disabled={!quote || quote.QuoteStatus === QuoteStatusEnum.Rejected}
          >
            {quoteCountdown ? `Trade (${formattedOrderTime(quoteCountdown * 1000)})` : 'Trade'}
          </GCashButton>
          <GCashButton
            size="lg"
            variant="tertiary"
            data-testid="swap-clear-button"
            onClick={() => {
              dispatch(resetSwapForm({}));
            }}
            className="w-full"
          >
            Clear
          </GCashButton>
        </div>
      </div>
    </Fragment>
  );
};
